// Clubhouse TV helpers shared by the screen, its panels and the admin editor.

// ClubDisplayPanelKind (API)
export const TV_PANEL = {
    Splash: 0,
    LiveNow: 1,
    ComingUp: 2,
    RecentResults: 3,
    LeagueStandings: 4,
    Ladder: 5,
    TournamentDraw: 6,
    AmericanoStandings: 7,
    Meet: 8,
    Challenges: 9,
    Leaderboard: 10,
    MatchReports: 11,
    LeagueGrid: 12,
};

// One identity icon per entity, as everywhere else in the product
export const TV_PANEL_ICON = {
    [TV_PANEL.Splash]: 'mdi-home-outline',
    [TV_PANEL.LiveNow]: 'mdi-broadcast',
    [TV_PANEL.ComingUp]: 'mdi-calendar-clock',
    [TV_PANEL.RecentResults]: 'mdi-scoreboard',
    [TV_PANEL.LeagueStandings]: 'mdi-table',
    [TV_PANEL.Ladder]: 'mdi-ladder',
    [TV_PANEL.TournamentDraw]: 'mdi-trophy',
    [TV_PANEL.AmericanoStandings]: 'mdi-account-switch',
    [TV_PANEL.Meet]: 'mdi-handshake',
    [TV_PANEL.Challenges]: 'mdi-sword-cross',
    [TV_PANEL.Leaderboard]: 'mdi-podium',
    [TV_PANEL.MatchReports]: 'mdi-newspaper-variant-outline',
    [TV_PANEL.LeagueGrid]: 'mdi-grid',
};

export const TV_PANEL_KIND_NAME = {
    [TV_PANEL.Splash]: 'Club welcome',
    [TV_PANEL.LiveNow]: 'Live now',
    [TV_PANEL.ComingUp]: 'Coming up',
    [TV_PANEL.RecentResults]: 'Latest results',
    [TV_PANEL.LeagueStandings]: 'League table',
    [TV_PANEL.Ladder]: 'Ladder',
    [TV_PANEL.TournamentDraw]: 'Tournament',
    [TV_PANEL.AmericanoStandings]: 'Americano table',
    [TV_PANEL.Meet]: 'Meet',
    [TV_PANEL.Challenges]: 'Ladder challenges',
    [TV_PANEL.Leaderboard]: 'Club leaderboard',
    [TV_PANEL.MatchReports]: 'Match reports',
    [TV_PANEL.LeagueGrid]: 'League results grid',
};

// Panels that only appear when there is something to show
export const TV_PANEL_SOMETIMES = [
    TV_PANEL.LiveNow, TV_PANEL.ComingUp, TV_PANEL.RecentResults, TV_PANEL.Challenges, TV_PANEL.Meet,
    TV_PANEL.MatchReports,
];

// Result/fixture competition kind (API): 0 league, 1 ladder, 2 tournament,
// 3 friendly, 4 meet, 5 americano
export const TV_COMPETITION_ICON = {
    0: 'mdi-table',
    1: 'mdi-ladder',
    2: 'mdi-trophy',
    3: 'mdi-account-multiple',
    4: 'mdi-handshake',
    5: 'mdi-account-switch',
};

const toDate = (value) => (value ? new Date(value) : null);

// One fixed format so the server's render and the screen's agree (24-hour,
// day before month)
const LOCALE = 'en-GB';

export const tvClock = (date = new Date()) =>
    date.toLocaleTimeString(LOCALE, {hour: '2-digit', minute: '2-digit', hourCycle: 'h23'});

export const tvLongDate = (date = new Date()) =>
    date.toLocaleDateString(LOCALE, {weekday: 'long', day: 'numeric', month: 'long'});

const sameDay = (a, b) => a.toDateString() === b.toDateString();

/** "Today 18:00", "Tomorrow 19:30", "Thu 18:00", "12 Oct 18:00". */
export const tvWhen = (value) => {
    const date = toDate(value);
    if (!date) return '';
    const now = new Date();
    const time = tvClock(date);
    if (sameDay(date, now)) return `Today ${time}`;
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    if (sameDay(date, tomorrow)) return `Tomorrow ${time}`;
    const days = (date - now) / 86_400_000;
    if (days > 0 && days < 6) return `${date.toLocaleDateString(LOCALE, {weekday: 'short'})} ${time}`;
    return `${date.toLocaleDateString(LOCALE, {day: 'numeric', month: 'short'})} ${time}`;
};

/** "22 Jun": the day a result was played, for the crawl. */
export const tvDayMonth = (value) => {
    const date = toDate(value);
    return date ? date.toLocaleDateString(LOCALE, {day: 'numeric', month: 'short'}) : '';
};

/** "2 hours ago", "yesterday", or a date. */
export const tvAgo = (value) => {
    const date = toDate(value);
    if (!date) return '';
    const minutes = Math.round((Date.now() - date) / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.round(hours / 24);
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString(LOCALE, {day: 'numeric', month: 'short'});
};

/** Winner side by points, never by parsing the result text. */
export const tvWinner = (result) => {
    const home = result?.homePoints;
    const away = result?.awayPoints;
    if (home == null || away == null || home === away) return null;
    return home > away ? 'home' : 'away';
};

/** The score line for a result: game scores when there are few, else the result text. */
export const tvScore = (result) => {
    const home = result?.homeResult ?? '';
    const away = result?.awayResult ?? '';
    return {home, away};
};

/** Splits rows into pages of a fixed size (at least one page). */
export const tvPages = (rows, size) => {
    const list = rows ?? [];
    if (list.length <= size) return [list];
    const pages = [];
    for (let i = 0; i < list.length; i += size) pages.push(list.slice(i, i + size));
    return pages;
};

/** A readable foreground for text on the accent colour. */
export const tvOnAccent = (hex) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    if (!m) return '#ffffff';
    const n = parseInt(m[1], 16);
    const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    return lum > 0.6 ? '#14181f' : '#ffffff';
};

// Rows per page in the feature area, by how the panel is laid out:
//  wide     the whole feature area of a landscape screen
//  column   one of two side-by-side columns on a landscape screen
//  portrait the feature area of a portrait screen (narrow but taller)
const PAGE_SIZES = {
    wide: {table: 12, ladder: 24, challenges: 7, live: 4, groups: 4},
    column: {table: 12, ladder: 12, challenges: 7, live: 4, groups: 2},
    portrait: {table: 13, ladder: 13, challenges: 8, live: 4, groups: 4},
};

export const tvPageSize = (key, mode = 'wide') => PAGE_SIZES[mode]?.[key] ?? PAGE_SIZES.wide[key];

// StageType (API): 1 = group; anything else is a knockout round
const STAGE_GROUP = 1;

/** The pages of a tournament draw: group tables while the groups are being
 * played (four to a page), then the knockout rounds as one bracket page. */
export const tvDrawPages = (draw, mode = 'wide') => {
    const stages = draw?.stages ?? [];
    const groups = stages.filter(s => s.type === STAGE_GROUP);
    const knockout = stages.filter(s => s.type !== STAGE_GROUP && s.fixtures?.length);
    const groupsLive = groups.some(g => g.fixtures?.some(f => f.homeResult == null));
    const pages = [];
    if (groups.length && (groupsLive || !knockout.length)) {
        for (const chunk of tvPages(groups, tvPageSize('groups', mode))) pages.push({groups: chunk});
    }
    if (knockout.length) pages.push({knockout: knockout.slice(mode === 'wide' ? -3 : -2)});
    return pages.length ? pages : [{groups: []}];
};

/** How many pages a panel turns through (each page shows for the panel's seconds). */
/** Rows per page for one panel: a join code's footer takes about three rows. */
export const tvRowsPerPage = (panel, key, mode = 'wide') => {
    const size = tvPageSize(key, mode);
    if (!panel?.join || !['table', 'ladder'].includes(key)) return size;
    return size - (mode === 'wide' && key === 'ladder' ? 6 : 3);
};

export const tvPanelPageCount = (panel, view, mode = 'wide') => {
    const size = (key) => tvRowsPerPage(panel, key, mode);
    switch (panel?.kind) {
        case TV_PANEL.LeagueStandings: return tvPages(panel.standings?.rows, size('table')).length;
        case TV_PANEL.AmericanoStandings: return tvPages(panel.americano?.rows, size('table')).length;
        case TV_PANEL.Ladder: return tvPages(panel.ladder?.rows, size('ladder')).length;
        case TV_PANEL.TournamentDraw: return tvDrawPages(panel.draw, mode).length;
        case TV_PANEL.Challenges: return tvPages(panel.challenges, size('challenges')).length;
        case TV_PANEL.LiveNow: return tvPages(view?.live, size('live')).length;
        // One report per page, the latest three
        case TV_PANEL.MatchReports: return Math.max(1, Math.min(3, panel.reports?.length ?? 0));
        default: return 1;
    }
};

// ── Packing the feature area ────────────────────────────────────────────────
// Short panels share the feature area rather than leaving it half empty. On a
// landscape screen it holds two columns side by side, each stacking panels
// while they fit; on a portrait screen, one column. A panel that would turn
// pages in a column (or is a tournament bracket) gets the whole area instead.
const FEATURE_HEIGHT = {landscape: 776, portrait: 836};
const STRAP = 100; // section header bar + body padding
const ROW = 54;
const PACK_GAP = 24;

/** Estimated height of a one-page panel laid out narrow (column or portrait),
 * or null when it should always have the area to itself. */
export const tvPanelHeight = (panel, view) => {
    const base = tvPanelBodyHeight(panel, view);
    // A join code adds the card's footer
    return base == null ? null : base + (panel.join ? JOIN_FOOTER : 0);
};

const JOIN_FOOTER = 154;

const tvPanelBodyHeight = (panel, view) => {
    const rows = (n) => STRAP + 34 + n * ROW;
    switch (panel?.kind) {
        case TV_PANEL.LeagueStandings: return rows(panel.standings?.rows?.length ?? 0);
        case TV_PANEL.AmericanoStandings: return rows(panel.americano?.rows?.length ?? 0);
        case TV_PANEL.Leaderboard: return rows(Math.min(9, panel.stats?.rows?.length ?? 0));
        case TV_PANEL.Ladder: return STRAP + (panel.ladder?.rows?.length ?? 0) * ROW;
        case TV_PANEL.Challenges: return STRAP + (panel.challenges?.length ?? 0) * 124;
        case TV_PANEL.Meet: return STRAP + 3 * ROW;
        case TV_PANEL.LiveNow: return STRAP + (view?.live?.length ?? 0) * 170;
        // Match reports always have the whole feature area to themselves
        case TV_PANEL.MatchReports: return null;
        // A small grid fits a column; a bigger one takes the whole width
        case TV_PANEL.LeagueGrid: {
            const n = panel.grid?.teams?.length ?? 0;
            return n <= 5 ? STRAP + 36 + n * tvGridSize(n, 'column').row : null;
        }
        default: return null;
    }
};

/** Groups the rotation into screens: {columns: [[panel, ...], ...], mode, pages}. */
export const tvScreens = (panels, view, portrait = false) => {
    const narrow = portrait ? 'portrait' : 'column';
    const limit = FEATURE_HEIGHT[portrait ? 'portrait' : 'landscape'];
    const maxColumns = portrait ? 1 : 2;
    const screens = [];
    let open = null; // the packed screen being filled
    let used = 0; // height used in its last column

    for (const panel of panels) {
        const height = tvPanelPageCount(panel, view, narrow) > 1 ? null : tvPanelHeight(panel, view);
        if (height == null || height > limit) {
            const mode = portrait ? 'portrait' : 'wide';
            screens.push({columns: [[panel]], mode, pages: tvPanelPageCount(panel, view, mode)});
            open = null;
            continue;
        }
        if (open && used + PACK_GAP + height <= limit) {
            open.columns[open.columns.length - 1].push(panel);
            used += PACK_GAP + height;
        } else if (open && open.columns.length < maxColumns) {
            open.columns.push([panel]);
            used = height;
        } else {
            open = {columns: [[panel]], mode: narrow, pages: 1};
            used = height;
            screens.push(open);
        }
    }
    // A packed screen that only filled one column takes the whole width instead
    for (const screen of screens) {
        if (screen.mode === 'column' && screen.columns.length === 1) screen.mode = 'wide';
    }
    return screens;
};

// ── Team names ──────────────────────────────────────────────────────────────
// A pair's name arrives as "Anna Smith + Ben Jones" (a custom team name has no
// " + " and is shown as it is).

/** The players in a team name: one entry, or each player of a pair. */
export const tvTeamParts = (name) => {
    const parts = String(name ?? '').split(' + ').map(p => p.trim()).filter(Boolean);
    return parts.length >= 2 && parts.length <= 4 ? parts : [String(name ?? '')];
};

/** "Anna Smith" -> "A. Smith"; "Anna S." and one-word names stay as they are. */
const initialled = (person) => {
    const words = person.split(/\s+/);
    if (words.length < 2 || /\.$/.test(words[words.length - 1])) return person;
    return `${words[0][0]}. ${words.slice(1).join(' ')}`;
};

/** A pair on one line where there's no room to stack: "A. Smith & B. Jones". */
export const tvCompactTeamName = (name) => {
    const parts = tvTeamParts(name);
    return parts.length > 1 ? parts.map(initialled).join(' & ') : parts[0];
};

/** Row sizes for a results grid, shrinking as divisions get bigger; taller
 * when the grid has the whole feature area to itself. */
export const tvGridSize = (n, mode = 'column') => {
    const wide = mode !== 'column';
    if (n <= 6) return wide ? {row: 66, font: 24} : {row: 50, font: 22};
    if (n <= 8) return wide ? {row: 60, font: 23} : {row: 46, font: 21};
    if (n <= 10) return wide ? {row: 50, font: 21} : {row: 42, font: 19};
    if (n <= 12) return {row: 42, font: 19};
    return {row: 34, font: 16};
};

/** A grid cell's text exactly as entered (scoring systems are club-defined, so
 * results can be any wording), with a font scale that fits it in the cell. */
export const tvGridCellScale = (text) => {
    const length = String(text ?? '').trim().length;
    return length <= 5 ? 1 : Math.max(0.5, 5 / length);
};
